import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Gauge, BarChart3, RefreshCw, Tags} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, Cell, LabelList, Tooltip, ResponsiveContainer
} from 'recharts';
import {
    COR, FONTE_DADOS, RAIO, cartao, raizDashboard, OverlayCarregando, conteudoCarregando,
    TituloBloco, MultiSelectLojas, rotuloCard, valorCard, rodapeCard, CARD_GAP,
} from './ui';

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

// =====================================================================
// PAINEL "DESEMPENHO DOS RESTAURANTES" (DEM-00012)
// =====================================================================
// Corpo da aba DESEMPENHO da Home (ver ./abas-home.js — PERFIS_DESEMPENHO
// decide quem a alcança; este componente só renderiza). Visão executiva do
// PDF "FORMA DE AVALIAR E CLASSIFICAR": tabela Restaurante × 9 indicadores (6
// áreas da Avaliação 360 + Segurança Alimentar + Treinamento e Pessoas +
// Gestão Financeira e de Estoques), Nota Geral por loja, Nota da Rede,
// distribuição das classificações e desempenho médio por área. Consome
// /api/home/operacao/painel-desempenho — TUDO derivado ali (a tela só
// formata, não soma nada) — EXCETO a "Média" da tabela quando o filtro de
// loja está ativo, recalculada aqui mesmo (ver `mediaVisiveis` abaixo): o
// backend só sabe somar a REDE inteira, e a tabela filtrada não pode mostrar
// uma média que não corresponde às linhas visíveis.
//
// ⚠️⚠️ TELA EM TESTE (Julia, 23/09): a aba só aparece para quem
// `PERFIS_DESEMPENHO` libera (Admin, Diretoria, Diretoria Op, Operacional) —
// SEM Gerente/Gerente Plantao por enquanto, ao contrário da aba Operação.
//
// MESMA CASCA VISUAL dos dashboards irmãos (Operação/RH), ponto a ponto —
// pedido da Julia em 23/09 depois da 1ª versão (que tinha estilo próprio):
//   - `raizDashboard`/`cartao`/`TituloBloco`/`rotuloCard`/`valorCard` de ./ui.js;
//   - cabeçalho só com o FILTRO (o nome da tela já está na aba, uma linha
//     acima — replica o comentário de DashboardOperacao: "o que dizia em qual
//     visão estamos é a barra de abas da Home");
//   - linha "Mostrando X lojas · período" logo abaixo do filtro;
//   - tabela com cabeçalho vermelho-escuro/branco e zebra nas linhas —
//     mesmo desenho da `TabelaSegmentos` da Operação.

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const fmtPct = (n) => (n === null || n === undefined ? '—' : `${Math.round(Number(n))}%`);

// RÉGUA DE COR — a mesma dos 4 instrumentos-fonte (classificar360, fonte
// única em aval360-score.js): Verde ≥90 · Amarelo ≥80 · Laranja ≥70 ·
// Vermelho <70. NA = sem avaliação aplicável (nunca confundir com 0%). A
// FAIXA (texto do intervalo) é só rótulo — vem do MESMO PDF que define a
// régua, não é recalculada: mudar aqui não muda classificação nenhuma.
//
// ⚠️ DUAS cores por classe, de propósito (Julia, 23/09: "o amarelo está muito
// parecido com o vermelho" nos pontinhos da tabela — eram a MESMA cor escura
// usada como texto E como marcador, e um amarelo escuro o bastante pra ler
// como texto (`#a16207`) fica indistinguível de vermelho/laranja num círculo
// de 8px):
//   `fg`  — texto/número sobre fundo claro (precisa de contraste: escuro).
//   `dot` — pontinho/anel/barra (não é texto, não precisa de contraste com o
//           fundo — só precisa ser bem diferente das outras 3 cores; usa a
//           faixa "500" do Tailwind, bem mais viva que a "700"/"800" do `fg`).
const FAIXAS = [
    { classe: 'Verde', fg: '#15803d', dot: '#22c55e', bg: '#dcfce7', faixa: '90% a 100%' },
    { classe: 'Amarelo', fg: '#a16207', dot: '#eab308', bg: '#fef9c3', faixa: '80% a 89,99%' },
    { classe: 'Laranja', fg: '#c2410c', dot: '#f97316', bg: '#ffedd5', faixa: '70% a 79,99%' },
    { classe: 'Vermelho', fg: '#dc2626', dot: '#ef4444', bg: '#fee2e2', faixa: 'Abaixo de 70%' },
    { classe: 'NA', fg: COR.subtitulo, dot: COR.subtitulo, bg: '#f1f5f9', faixa: 'Não aplicável' },
];
const CORES_CLASSE = Object.fromEntries(FAIXAS.map((f) => [f.classe, f]));
// Ordem de exibição das 4 faixas "reais" (sem NA) — usada no card de Nota da
// Rede (lojas por classificação) e no de Distribuição (células por classificação).
const ORDEM_DISTRIBUICAO = ['Verde', 'Amarelo', 'Laranja', 'Vermelho'];

// Classifica um percentual pela MESMA régua do backend (classificar360) —
// só usada para recalcular a linha "Média" quando o filtro de loja corta a
// tabela (ver o comentário grande no topo do arquivo). A fonte da verdade
// segue sendo o backend; isto é só o rótulo de UMA célula derivada na tela.
const classificarPct = (pct) => {
    if (pct >= 90) return 'Verde';
    if (pct >= 80) return 'Amarelo';
    if (pct >= 70) return 'Laranja';
    return 'Vermelho';
};

// Média dos indicadores (e da Nota Geral) só das lojas VISÍVEIS na tabela —
// espelha `agregarRede`/`montarLinhaLoja` do backend (server/home/
// painel-desempenho-utils.js) em miniatura, porque aquele arquivo é server-
// side e não entra no bundle do front. Se a fórmula mudar lá, muda aqui.
function mediaVisiveis(lojasVisiveis) {
    if (lojasVisiveis.length === 0) return null;
    const codigos = lojasVisiveis[0].indicadores.map((i) => i.codigo);
    const porIndicador = codigos.map((codigo) => {
        const valores = lojasVisiveis
            .map((l) => l.indicadores.find((i) => i.codigo === codigo))
            .filter((i) => i && i.percentual !== null)
            .map((i) => i.percentual);
        const media = valores.length > 0 ? Number((valores.reduce((a, b) => a + b, 0) / valores.length).toFixed(2)) : null;
        return { codigo, percentual: media, classificacao: media === null ? 'NA' : classificarPct(media) };
    });
    const notasGerais = lojasVisiveis.map((l) => l.notaGeral).filter((n) => n !== null);
    const notaGeral = notasGerais.length > 0 ? Number((notasGerais.reduce((a, b) => a + b, 0) / notasGerais.length).toFixed(2)) : null;
    return { porIndicador, notaGeral, classificacaoGeral: notaGeral === null ? 'NA' : classificarPct(notaGeral) };
}

const selectStyle = {
    fontFamily: FONTE_DADOS, fontSize: '13px', fontWeight: 600, color: COR.texto,
    border: `1px solid ${COR.bordaSuave}`, borderRadius: '8px', padding: '7px 12px',
    backgroundColor: '#fff', cursor: 'pointer', outline: 'none',
};

function PainelDesempenho() {
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    // Filtro de loja — client-side (o endpoint já traz a rede inteira de uma
    // vez, sem paginação nem parâmetro de loja): recorta a TABELA na hora,
    // sem round-trip ao servidor. Vazio = todas. Os cards "da Rede" (Nota,
    // Distribuição, Por Área) NÃO seguem o filtro — o nome deles é a régua:
    // são sempre a rede inteira, o ponto de comparação.
    const [lojasFiltro, setLojasFiltro] = useState([]);
    // Filtro de ANO — este SIM vai ao servidor (a média é feita na query, não dá
    // para recortar no front). Padrão = ano corrente; a lista de anos com
    // avaliação vem no payload (`anosDisponiveis`).
    const [ano, setAno] = useState(new Date().getFullYear());
    const [anosDisponiveis, setAnosDisponiveis] = useState([]);
    // Filtros novos — vão ao servidor (recortam o painel inteiro: tabela, rede e
    // rank). Vazio = sem recorte. Mês 1..12; mercado 'Norte'|'Sul'.
    const [mes, setMes] = useState('');
    const [mercado, setMercado] = useState('');

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        try {
            const qs = `?ano=${ano}${mes ? `&mes=${mes}` : ''}${mercado ? `&mercado=${mercado}` : ''}`;
            const r = await fetch(`/api/home/operacao/painel-desempenho${qs}`, { headers: authHeaders() });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const json = await r.json();
            setDados(json);
            setAnosDisponiveis(json.anosDisponiveis || []);
        } catch (e) {
            console.error('[PainelDesempenho]', e);
            setErro('Não foi possível carregar o painel. Tente novamente.');
        } finally {
            setCarregando(false);
        }
    }, [ano, mes, mercado]);

    useEffect(() => { carregar(); }, [carregar]);

    const lojasVisiveis = useMemo(() => {
        if (!dados) return [];
        return lojasFiltro.length === 0 ? dados.lojas : dados.lojas.filter((l) => lojasFiltro.includes(l.loja));
    }, [dados, lojasFiltro]);

    const nomeFoco = lojasFiltro.length === 0 ? 'Rede (todas as lojas)'
        : lojasFiltro.length === 1 ? lojasFiltro[0]
            : `${lojasFiltro.length} lojas (${lojasFiltro.join(', ')})`;

    return (
        <div style={{ ...raizDashboard, marginBottom: '28px' }}>
            {/* Cabeçalho: só o FILTRO — o nome da tela já está na barra de abas da
                Home, uma linha acima (mesma convenção da Operação/RH). */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '12px', marginBottom: '18px' }}>
                {/* Seletor de ANO — a lista já vem do backend com o ano corrente
                    garantido; se o payload ainda não chegou, mostra ao menos o ano
                    selecionado para o campo nunca ficar vazio. */}
                <select
                    value={mercado}
                    onChange={(e) => setMercado(e.target.value)}
                    style={selectStyle}
                    disabled={carregando}
                    aria-label="Mercado"
                >
                    <option value="">Todos os mercados</option>
                    <option value="Norte">Norte</option>
                    <option value="Sul">Sul</option>
                </select>
                <select
                    value={mes}
                    onChange={(e) => setMes(e.target.value)}
                    style={selectStyle}
                    disabled={carregando}
                    aria-label="Mês"
                >
                    <option value="">Mês</option>
                    {MESES.map((nome, i) => <option key={nome} value={i + 1}>{nome}</option>)}
                </select>
                <select
                    value={ano}
                    onChange={(e) => setAno(Number(e.target.value))}
                    style={selectStyle}
                    disabled={carregando}
                    aria-label="Ano"
                >
                    {(anosDisponiveis.length ? anosDisponiveis : [ano]).map((a) => (
                        <option key={a} value={a}>{a}</option>
                    ))}
                </select>
                <MultiSelectLojas
                    lojas={(dados?.lojas || []).map((l) => l.loja)}
                    selecionados={lojasFiltro}
                    onChange={setLojasFiltro}
                    selectStyle={selectStyle}
                    desabilitado={carregando}
                />
            </div>

            {erro && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#dc2626', backgroundColor: '#fee2e2', borderRadius: RAIO, padding: '14px 18px', marginBottom: '16px' }}>
                    {erro}
                    <button onClick={carregar} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 700 }}>
                        <RefreshCw size={14} /> tentar de novo
                    </button>
                </div>
            )}

            {!erro && (
                <div style={{ position: 'relative', minHeight: carregando ? '320px' : 0 }}>
                    <div style={conteudoCarregando(carregando)}>
                        {dados && (
                            <>
                                {/* Linha de contexto: recorte em foco + legenda de classificação —
                                    mesmo lugar/estilo da linha "Mostrando..." da Operação/RH. */}
                                <div style={{ fontSize: '13px', color: '#888', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                                    <span style={{ flex: 1, minWidth: '220px' }}>
                                        Mostrando <strong style={{ color: COR.texto }}>{nomeFoco}</strong>
                                        {' · '}Média de <strong style={{ color: COR.texto }}>{ano}</strong>
                                    </span>
                                    <LegendaClassificacao />
                                </div>

                                <TabelaDesempenho lojas={lojasVisiveis} media={mediaVisiveis(lojasVisiveis)} />

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '16px' }}>
                                    <BlocoNotaRede rede={dados.rede} lojas={dados.lojas} />
                                    <BlocoRank lojas={dados.lojas} />
                                    <BlocoPorArea porArea={dados.rede.porArea} />
                                </div>
                            </>
                        )}
                    </div>
                    {carregando && <OverlayCarregando texto="Carregando o painel…" />}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------
// LEGENDA — o "card de classificação" do PDF: as 4 faixas de cor + N/A, num
// carimbo compacto (cartao). É só CONSULTA — nada aqui muda a régua, que
// vem de classificar360() no backend.
// ---------------------------------------------------------------------
function LegendaClassificacao() {
    return (
        <div style={{ ...cartao, display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 14px', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', ...rotuloCard, color: COR.subtitulo }}>
                <Tags size={13} /> CLASSIFICAÇÃO
            </span>
            {FAIXAS.map((f) => (
                <span key={f.classe} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>
                    <span style={{ width: '9px', height: '9px', borderRadius: '50%', backgroundColor: f.dot, flexShrink: 0 }} />
                    {f.faixa}
                </span>
            ))}
        </div>
    );
}

// ---------------------------------------------------------------------
// TABELA — Restaurante × 9 indicadores + Nota Geral, linha Média no rodapé.
// Mesmo desenho da `TabelaSegmentos` (Operação): cabeçalho vermelho-escuro/
// branco, zebra nas linhas, rodapé destacado.
// ---------------------------------------------------------------------
function TabelaDesempenho({ lojas, media }) {
    if (lojas.length === 0) {
        return (
            <div style={{ ...cartao, padding: '32px', textAlign: 'center', color: '#999' }}>
                Nenhuma loja com avaliação registrada neste ano.
            </div>
        );
    }
    const th = { padding: '10px 12px', fontSize: '12px', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap' };
    const thEsq = { ...th, textAlign: 'left' };
    const td = { padding: '9px 12px', textAlign: 'center', whiteSpace: 'nowrap' };
    const tdEsq = { ...td, textAlign: 'left', fontWeight: 700, color: COR.texto };
    const labels = lojas[0].indicadores.map((i) => i.label);

    return (
        <div style={{ ...cartao, padding: '4px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                <thead>
                    <tr style={{ backgroundColor: COR.vermelhoEscuro, color: '#fff' }}>
                        <th style={thEsq}>Restaurante</th>
                        {labels.map((label) => <th key={label} style={th}>{label}</th>)}
                        <th style={th}>Nota Geral</th>
                    </tr>
                </thead>
                <tbody>
                    {lojas.map((l, i) => (
                        <tr key={l.loja} style={{ backgroundColor: i % 2 ? '#faf7f7' : '#fff' }}>
                            <td style={tdEsq}>{l.loja}</td>
                            {l.indicadores.map((ind) => (
                                <td key={ind.codigo} style={td}><CelulaIndicador indicador={ind} /></td>
                            ))}
                            <td style={td}><CelulaIndicador indicador={{ percentual: l.notaGeral, classificacao: l.classificacaoGeral }} negrito /></td>
                        </tr>
                    ))}
                </tbody>
                {media && (
                    <tfoot>
                        <tr style={{ backgroundColor: COR.amareloFundo, borderTop: `2px solid ${COR.bordaSuave}` }}>
                            <td style={{ ...tdEsq, fontWeight: 800 }}>Média</td>
                            {media.porIndicador.map((ind) => (
                                <td key={ind.codigo} style={td}><CelulaIndicador indicador={ind} negrito /></td>
                            ))}
                            <td style={td}><CelulaIndicador indicador={{ percentual: media.notaGeral, classificacao: media.classificacaoGeral }} negrito /></td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}

// Célula-pílula: bolinha da cor da classificação + percentual (ou "N/A").
// `tabular-nums` já vem da raiz do dashboard (raizDashboard), herdado.
function CelulaIndicador({ indicador, negrito = false }) {
    const cor = CORES_CLASSE[indicador.classificacao] || CORES_CLASSE.NA;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: cor.dot, flexShrink: 0 }} />
            <span style={{ fontWeight: negrito ? 700 : 500, color: indicador.percentual === null ? COR.subtitulo : COR.texto }}>
                {indicador.percentual === null ? 'N/A' : fmtPct(indicador.percentual)}
            </span>
        </span>
    );
}

// ---------------------------------------------------------------------
// BLOCO 1 — Nota da Rede: anel de progresso (CSS puro) + classificação/faixa
// + total de lojas avaliadas + lojas POR classificação. Ampliado 2x (Julia,
// 23/09 e depois de novo ao ver o print): a 1ª rodada só acrescentou texto
// ao lado do anel e AINDA sobrava metade do card vazia — o card não tinha
// conteúdo suficiente pro tamanho, não faltava só arrumar o layout. A coluna
// nova ("lojas por classificação") usa a largura que sobrava e é dado NOVO,
// não repetido: aqui é CONTAGEM DE LOJAS pela Nota Geral; a "Distribuição"
// ao lado conta CÉLULAS (indicador × loja) — números diferentes de propósito.
// ---------------------------------------------------------------------
function BlocoNotaRede({ rede, lojas }) {
    const cor = CORES_CLASSE[rede.classificacaoRede] || CORES_CLASSE.NA;
    const pct = rede.notaRede ?? 0;
    // Toda loja que chega até aqui já tem ≥1 indicador apurado.
    const contagemPorClasse = ORDEM_DISTRIBUICAO.reduce((acc, classe) => {
        acc[classe] = lojas.filter((l) => l.classificacaoGeral === classe).length;
        return acc;
    }, {});
    
    return (
        <Bloco icone={<Gauge size={16} />} titulo="Nota Geral da Rede">
            <div style={{ display: 'flex', alignItems: 'stretch', gap: '20px', flexWrap: 'wrap', height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{
                        width: '150px', height: '150px', borderRadius: '50%', flexShrink: 0,
                        background: `conic-gradient(${cor.dot} ${pct * 3.6}deg, ${COR.buscaFundo} 0deg)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <div style={{ width: '116px', height: '116px', borderRadius: '50%', backgroundColor: COR.branco, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ ...valorCard, fontSize: '32px', color: COR.texto }}>{rede.notaRede === null ? '—' : `${Math.round(rede.notaRede)}%`}</span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                            <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '999px', backgroundColor: cor.bg, color: cor.fg, fontWeight: 700, fontSize: '13px' }}>
                                {rede.classificacaoRede === 'NA' ? 'Sem dados' : rede.classificacaoRede}
                            </div>
                            <div style={{ ...rodapeCard, marginTop: '4px' }}>{cor.faixa}</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: CARD_GAP }}>
                            <span style={rotuloCard}>LOJAS AVALIADAS</span>
                            <span style={{ ...valorCard, fontSize: '19px', color: COR.texto }}>{lojas.length}</span>
                        </div>
                    </div>
                </div>
                {/* Lojas por classificação — preenche a largura que sobrava ao
                    lado do anel com uma informação nova (ver comentário acima). */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: '150px', justifyContent: 'center', borderLeft: `1px solid ${COR.bordaSuave}`, paddingLeft: '18px' }}>
                    <span style={rotuloCard}>LOJAS POR CLASSIFICAÇÃO</span>
                    {ORDEM_DISTRIBUICAO.map((classe) => {
                        const c = CORES_CLASSE[classe];
                        return (
                            <div key={classe} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ width: '9px', height: '9px', borderRadius: '50%', backgroundColor: c.dot, flexShrink: 0 }} />
                                <span style={{ flex: 1, fontSize: '12px', color: COR.texto, fontWeight: 'bold' }}>{classe}</span>
                                <span style={{ ...valorCard, fontSize: '15px', color: COR.texto }}>{contagemPorClasse[classe]}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Bloco>
    );
}

// ---------------------------------------------------------------------
// BLOCO 2 — Ranking das lojas por Nota Geral: Top 5 e Piores 5. Segue os filtros
// mercado + mês (já aplicados no backend em `dados.lojas`). Substituiu a
// Distribuição das Classificações (Julia, 28/09). As barras usam a paleta de
// STATUS (a régua Verde/Amarelo/Laranja/Vermelho) — a legenda da régua no topo
// do painel explica a cor, e o valor % vai no rótulo, então a identidade nunca é
// só cor.
// ---------------------------------------------------------------------
function RankChart({ titulo, data }) {
    const altura = Math.max(110, data.length * 34 + 24);
    return (
        <div>
            <div style={{ fontSize: '11px', fontWeight: 800, color: COR.subtitulo, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' }}>{titulo}</div>
            <ResponsiveContainer width="100%" height={altura}>
                <BarChart layout="vertical" data={data} margin={{ top: 2, right: 44, bottom: 2, left: 4 }} barCategoryGap={6}>
                    <XAxis type="number" domain={[0, 100]} hide />
                    <YAxis type="category" dataKey="loja" width={46} tick={{ fontSize: 12, fontWeight: 700, fill: COR.texto }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} formatter={(v) => [`${v}%`, 'Nota Geral']} />
                    <Bar dataKey="nota" radius={[0, 4, 4, 0]} barSize={16} isAnimationActive={false}>
                        {data.map((d) => <Cell key={d.loja} fill={d.fill} />)}
                        <LabelList dataKey="nota" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 11, fontWeight: 700, fill: COR.texto }} />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

// Alternador Top ↔ Piores (uma visão por vez).
function ToggleTopBottom({ visao, onChange }) {
    const btn = (ativo) => ({
        padding: '4px 14px', borderRadius: '6px', border: 'none', cursor: 'pointer',
        fontSize: '12px', fontWeight: 700,
        background: ativo ? COR.texto : 'transparent',
        color: ativo ? '#fff' : COR.subtitulo,
    });
    return (
        <div style={{ display: 'inline-flex', gap: '2px', padding: '3px', background: COR.buscaFundo, borderRadius: '8px', marginBottom: '12px' }}>
            <button type="button" style={btn(visao === 'top')} onClick={() => onChange('top')}>Top 5</button>
            <button type="button" style={btn(visao === 'bottom')} onClick={() => onChange('bottom')}>Piores 5</button>
        </div>
    );
}

function BlocoRank({ lojas }) {
    const [visao, setVisao] = useState('top');

    // Só lojas COM Nota Geral (sem nota não entra no rank), ordenadas da maior
    // para a menor.
    const rankeadas = (lojas || [])
        .filter((l) => l.notaGeral != null)
        .map((l) => ({ loja: l.loja, nota: l.notaGeral, classe: l.classificacaoGeral }))
        .sort((a, b) => b.nota - a.nota);

    const top = rankeadas.slice(0, 5);
    // "Piores" começa depois do Top — assim, com poucas lojas (mercado filtrado),
    // uma loja não aparece nos dois. Mostradas da pior para a menos pior.
    const inicioBottom = Math.max(5, rankeadas.length - 5);
    const bottom = rankeadas.slice(inicioBottom).reverse();

    // Cor da barra: no Top, a régua (status) da loja; nos Piores, TUDO vermelho —
    // é uma lista de alerta, então padroniza no vermelho (Julia, 28/09).
    const corDaBarra = (classe) => (
        visao === 'bottom'
            ? CORES_CLASSE.Vermelho.fg
            : (CORES_CLASSE[classe] || CORES_CLASSE.NA).fg
    );

    const base = visao === 'top' ? top : bottom;
    const data = base.map((r) => ({ loja: r.loja, nota: r.nota, fill: corDaBarra(r.classe) }));

    return (
        <Bloco icone={<BarChart3 size={16} />} titulo="Ranking por Nota Geral">
            {rankeadas.length === 0 ? (
                <div style={{ color: '#999', fontSize: '13px', padding: '8px 0' }}>Sem lojas com nota no período.</div>
            ) : (
                <>
                    <ToggleTopBottom visao={visao} onChange={setVisao} />
                    <RankChart titulo={visao === 'top' ? `Top ${data.length}` : `Piores ${data.length}`} data={data} />
                </>
            )}
        </Bloco>
    );
}

// ---------------------------------------------------------------------
// BLOCO 3 — Desempenho por área: média da rede em cada um dos 9 indicadores,
// em barras horizontais coloridas pela régua.
// ---------------------------------------------------------------------
function BlocoPorArea({ porArea }) {
    return (
        <Bloco icone={<BarChart3 size={16} />} titulo="Desempenho por Área (média da rede)">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {porArea.map((a) => {
                    const cor = CORES_CLASSE[a.classificacao] || CORES_CLASSE.NA;
                    const largura = a.media === null ? 0 : a.media;
                    return (
                        <div key={a.codigo} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                            <span style={{ width: '78px', flexShrink: 0, color: COR.texto, fontWeight: 'bold' }}>{a.label}</span>
                            <div style={{ flex: 1, height: '10px', borderRadius: '999px', backgroundColor: COR.buscaFundo, overflow: 'hidden' }}>
                                <div style={{ width: `${largura}%`, height: '100%', backgroundColor: cor.dot, borderRadius: '999px' }} />
                            </div>
                            <span style={{ width: '34px', textAlign: 'right', fontWeight: 700, color: COR.texto }}>{fmtPct(a.media)}</span>
                        </div>
                    );
                })}
            </div>
        </Bloco>
    );
}

function Bloco({ icone, titulo, children }) {
    return (
        <div style={{ ...cartao, padding: '16px 18px' }}>
            <div style={{ marginBottom: '14px' }}>
                <TituloBloco icone={icone} texto={titulo} />
            </div>
            {children}
        </div>
    );
}

export default PainelDesempenho;
